import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

// use LOCAL worker
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.mjs";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
function cropCanvasToBlob(canvas, rect) {
  return new Promise((resolve) => {
    const temp = document.createElement("canvas");
    temp.width = rect.w;
    temp.height = rect.h;

    const ctx = temp.getContext("2d");

    ctx.drawImage(
      canvas,
      rect.x,
      rect.y,
      rect.w,
      rect.h,
      0,
      0,
      rect.w,
      rect.h
    );

    temp.toBlob((blob) => {
      resolve(blob);
    }, "image/png");
  });
}
/**
 * Convert CANVAS-PIXEL rect → relative rect (snake_case)
 * Backend + python expect this format
 */
function toRelRect(pxRect, canvasW, canvasH) {
  if (!pxRect || canvasW <= 0 || canvasH <= 0) return null;

  const x1 = clamp(pxRect.x, 0, canvasW);
  const y1 = clamp(pxRect.y, 0, canvasH);
  const x2 = clamp(pxRect.x + pxRect.w, 0, canvasW);
  const y2 = clamp(pxRect.y + pxRect.h, 0, canvasH);

  const padding = 2;

  const left = Math.min(x1, x2) + padding;
  const top = Math.min(y1, y2) + padding;
  const right = Math.max(x1, x2) - padding;
  const bottom = Math.max(y1, y2) - padding;

  const w = right - left;
  const h = bottom - top;
  if (w < 2 || h < 2) return null;

  return {
    rel_left: left / canvasW,
    rel_top: top / canvasH,
    rel_width: w / canvasW,
    rel_height: h / canvasH,
  };
}

/** Convert CSS mouse coords → canvas pixel coords */
function getCanvasPoint(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY,
  };
}

/** Convert canvas pixel rect → CSS rect (for overlay display) */
function toCssRect(pxRect, canvas) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = rect.width / canvas.width;
  const scaleY = rect.height / canvas.height;

  return {
    x: pxRect.x * scaleX,
    y: pxRect.y * scaleY,
    w: pxRect.w * scaleX,
    h: pxRect.h * scaleY,
  };
}

const PdfMaskPreview = forwardRef(({
  file,
  editMode,
  previewZoom,
  rectsPx,
  setRectsPx,
  activeDrag,
  setActiveDrag,
  onRelRectsChange,
  onCanvasSizeChange,
  onSpotRectDraw,
  onPageChange,
  currentPage,
  templatePolygons = [],
}, ref) => {
  const canvasRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getCanvasImage: () => {
      if (!canvasRef.current) return null;
      return canvasRef.current.toDataURL("image/png").split(",")[1];
    }
  }));

  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const canDraw = editMode !== "normal";

  const containerRef = useRef(null);
  const [fitWidth, setFitWidth] = useState(800);

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      setFitWidth(containerRef.current.clientWidth);
    };

    measure();
    window.addEventListener("resize", measure);

    return () => window.removeEventListener("resize", measure);
  }, []);


  /* ================= LOAD PDF ================= */
  useEffect(() => {
    setRectsPx([]);
    setActiveDrag(null);
    setPdfDoc(null);
    setPageNum(1);
    setNumPages(1);
    setErr(null);

    if (!file) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const buf = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        if (!cancelled) {
          setPdfDoc(doc);
          setNumPages(doc.numPages);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => (cancelled = true);
  }, [file, setRectsPx, setActiveDrag]);

  useEffect(() => {
    const requestedPage = Number(currentPage);
    if (!Number.isInteger(requestedPage)) return;
    const nextPage = clamp(requestedPage, 1, numPages);
    setPageNum((page) => page === nextPage ? page : nextPage);
  }, [currentPage, numPages]);

  /* ================= RENDER PAGE ================= */
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;

        // Natural PDF size
        const baseViewport = page.getViewport({ scale: 1 });

        // Fit page to container width
        const fitScale = fitWidth / baseViewport.width;

        // Apply zoom slider
        const finalScale = fitScale * previewZoom;

        // Final viewport
        const viewport = page.getViewport({ scale: finalScale });


        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        // 🔥 THIS IS THE SIZE PYTHON MUST USE
        onCanvasSizeChange?.({ w: canvas.width, h: canvas.height });

        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => (cancelled = true);
  }, [pdfDoc, pageNum, previewZoom, fitWidth, onCanvasSizeChange]);

  /* ================= REL RECT COMPUTE ================= */
  const relRects = useMemo(() => {
    const canvas = canvasRef.current;
    if (!canvas) return [];
    return rectsPx
      .map((r) => toRelRect(r, canvas.width, canvas.height))
      .filter(Boolean);
  }, [rectsPx]);

  useEffect(() => {
    onRelRectsChange?.(relRects);
  }, [relRects, onRelRectsChange]);

  /* ================= MOUSE ================= */
  const onMouseDown = (e) => {
    if (!canDraw) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasPoint(e, canvas);
    setActiveDrag({ startX: x, startY: y, curX: x, curY: y });
  };

  const onMouseMove = (e) => {
    if (!activeDrag) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasPoint(e, canvas);
    setActiveDrag((d) => (d ? { ...d, curX: x, curY: y } : d));
  };

  const onMouseUp = () => {
    if (!activeDrag) return;

    const x = Math.floor(Math.min(activeDrag.startX, activeDrag.curX));
    const y = Math.floor(Math.min(activeDrag.startY, activeDrag.curY));
    const w = Math.floor(Math.abs(activeDrag.curX - activeDrag.startX));
    const h = Math.floor(Math.abs(activeDrag.curY - activeDrag.startY));

    setActiveDrag(null);

    const canvas = canvasRef.current;
    if (!canvas) return;

    if (editMode === "spot") {
      const rect = { x, y, w, h };
      setRectsPx([rect]);
      cropCanvasToBlob(canvas, rect).then((blob) => {
        onSpotRectDraw?.({ rect, blob });
      });
    } else {
      // ✅ Store as relative coordinates (0 to 1)
      const relX = x / canvas.width;
      const relY = y / canvas.height;
      const relW = w / canvas.width;
      const relH = h / canvas.height;

      setRectsPx((prev) => [
        ...prev,
        { x: relX, y: relY, w: relW, h: relH, page: pageNum, isRelative: true }
      ]);
    }
  };

  /* ================= RENDER ================= */
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <button disabled={pageNum <= 1}
          onClick={() => {
            setPageNum(p => {
              const newPage = p - 1
              onPageChange?.(newPage)
              return newPage
            })
          }}>◀</button>
        <span style={{ margin: "0 8px" }}>
          Page {pageNum} / {numPages}
        </span>
        <button disabled={pageNum >= numPages} onClick={() => {
          setPageNum(p => {
            const newPage = p + 1
            onPageChange?.(newPage)
            return newPage
          })
        }}>▶</button>
        {loading && <span style={{ marginLeft: 8 }}>Loading...</span>}
      </div>

      {err && <div style={{ color: "red" }}>{err}</div>}

      <div
        ref={containerRef}
        style={{
          position: "relative",
          overflow: "auto",
          width: "100%",
          textAlign: "center", // Center the document if it's smaller than the view
          background: "var(--bg-preview-inner)",
          padding: "20px 0",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        {/* ✅ WRAPPER: Glue everything to the document itself */}
        <div
          style={{
            position: "relative",
            display: "inline-block",
            verticalAlign: "top",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: "block",
              cursor: canDraw ? "crosshair" : "default",
            }}
          />

          {/* ✅ UNIFIED SVG LAYER (Manual Masks + Template Masks) */}
          <svg
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            {/* 1. Manual Masks */}
            {rectsPx
              .filter(r => r.isRelative && (!r.page || r.page === pageNum))
              .map((r, i) => (
                <rect
                  key={`manual-${i}`}
                  x={r.x}
                  y={r.y}
                  width={r.w}
                  height={r.h}
                  fill="#000"
                />
              ))}

            {/* 2. Template Aligned Polygons */}
            {templatePolygons
              .filter(poly => !poly.page || poly.page === pageNum)
              .map((poly, idx) => {
                const points = poly.points
                  .map(p => `${p.rel_x},${p.rel_y}`)
                  .join(" ");
                return (
                  <polygon
                    key={`temp-${idx}`}
                    points={points}
                    fill="#000"
                    stroke="#000"
                    strokeWidth="0.001"
                  />
                );
              })}

            {/* 3. Active Drag Preview */}
            {activeDrag && (
              <rect
                x={Math.min(activeDrag.startX, activeDrag.curX) / (canvasRef.current?.width || 1)}
                y={Math.min(activeDrag.startY, activeDrag.curY) / (canvasRef.current?.height || 1)}
                width={Math.abs(activeDrag.curX - activeDrag.startX) / (canvasRef.current?.width || 1)}
                height={Math.abs(activeDrag.curY - activeDrag.startY) / (canvasRef.current?.height || 1)}
                fill={editMode === "mask" ? "rgba(0,0,0,0.4)" : "rgba(255,0,0,0.2)"}
                stroke={editMode === "mask" ? "#000" : "#f00"}
                strokeWidth="0.002"
                strokeDasharray="0.01,0.01"
              />
            )}
          </svg>
        </div>
      </div>
    </div>
  );
});

export default PdfMaskPreview;
